import { ref } from 'vue';
import type { Ref } from 'vue';

interface RecaptchaState {
    isLoaded: boolean;
    isLoading: boolean;
}

export function useRecaptchaV3(action: string): {
    execute: () => Promise<string>;
    preload: () => Promise<void>;
    state: Ref<RecaptchaState>;
} {
    const config = useRuntimeConfig();
    const state = ref<RecaptchaState>({ isLoaded: false, isLoading: false });

    function getSiteKey(): string | undefined {
        return config.public.recaptcha?.v3SiteKey as string | undefined;
    }

    async function ensureScript(): Promise<void> {
        const siteKey = getSiteKey();
        if (!siteKey || !environment('production')) return;

        if ((window as any).grecaptcha) return;

        await new Promise<void>(resolve => {
            const existing = document.querySelector<HTMLScriptElement>(
                'script[src^="https://www.google.com/recaptcha/api.js"]'
            );
            if (existing) {
                existing.addEventListener('load', () => resolve());
                if ((existing as HTMLScriptElement).dataset.loaded === 'true') resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
            script.async = true;
            script.defer = true;
            script.dataset.loaded = 'false';
            script.onload = () => {
                script.dataset.loaded = 'true';
                resolve();
            };
            document.head.appendChild(script);
        });
    }

    const execute = async (): Promise<string> => {
        const siteKey = getSiteKey();
        if (!siteKey || !environment('production')) return '';

        if (!(window as any).grecaptcha) {
            await ensureScript();
        }

        return await new Promise<string>((resolve, reject) => {
            const grecaptcha = (window as any).grecaptcha as
                | { ready: (cb: () => void) => void; execute: (k: string, o: { action: string }) => Promise<string> }
                | undefined;
            if (!grecaptcha) return resolve('');
            state.value.isLoading = true;
            grecaptcha.ready(async () => {
                // grecaptcha returns a thenable without .finally
                return Promise.resolve(grecaptcha.execute(siteKey, { action }))
                    .then(resolve)
                    .catch(reject)
                    .finally(() => state.value.isLoading = false);
            });
        });
    };

    // not loaded on mount: the script is ~350KB and ran before first paint on every page (footer form).
    // forms call this on first focus so it's warm by the time they submit; execute() loads it regardless.
    const preload = async (): Promise<void> => {
        await ensureScript();
        state.value.isLoaded = true;
    };

    return { execute, preload, state };
}
